import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Briefcase, 
  Plus,
  Trash2,
  Loader2,
  Sparkles
} from 'lucide-react';
import { createJob, generateJobDescription } from '../../services/api';
import { toast } from 'sonner';

const CreateJob = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState({
    title: '',
    description: '',
    requirements: [''],
    benefits: [''],
    tasks: [''],
    location: '',
    job_type: 'Vollzeit',
    salary_range: '',
    languages_required: ['Deutsch'],
    experience_level: '',
    education_required: '',
    remote_option: false
  });

  const handleArrayAdd = (field) => {
    setJob({ ...job, [field]: [...job[field], ''] });
  };

  const handleArrayRemove = (field, index) => {
    setJob({ 
      ...job, 
      [field]: job[field].filter((_, i) => i !== index) 
    });
  };

  const handleArrayChange = (field, index, value) => {
    const updated = [...job[field]];
    updated[index] = value;
    setJob({ ...job, [field]: updated });
  };

  const handleGenerate = async () => {
    if (!job.title) {
      toast.error('Bitte geben Sie einen Jobtitel ein');
      return;
    }

    setGenerating(true);
    try {
      const res = await generateJobDescription({
        title: job.title,
        industry: '',
        tasks_summary: job.tasks.filter(t => t).join(', '),
        requirements_summary: job.requirements.filter(r => r).join(', ')
      });
      
      // Try to parse JSON response
      try {
        const generated = JSON.parse(res.data.generated_content);
        setJob({
          ...job,
          description: generated.description || job.description,
          tasks: generated.tasks?.length ? generated.tasks : job.tasks,
          requirements: generated.requirements?.length ? generated.requirements : job.requirements,
          benefits: generated.benefits?.length ? generated.benefits : job.benefits
        });
        toast.success('Stellenbeschreibung generiert');
      } catch {
        // If not JSON, use as description
        setJob({ ...job, description: res.data.generated_content });
        toast.success('Beschreibung generiert');
      }
    } catch (error) {
      toast.error('Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!job.title || !job.description || !job.location) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setLoading(true);
    try {
      const jobData = {
        ...job,
        requirements: job.requirements.filter(r => r.trim()),
        benefits: job.benefits.filter(b => b.trim()),
        tasks: job.tasks.filter(t => t.trim()),
        languages_required: job.languages_required.filter(l => l.trim())
      };
      
      await createJob(jobData);
      toast.success('Stellenanzeige erstellt');
      navigate('/employer/jobs');
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="create-job-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Neue Stellenanzeige</h1>
          <p className="text-muted-foreground mt-1">
            Erstellen Sie eine neue Jobanzeige mit KI-Unterstützung
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Grundinformationen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Jobtitel *</Label>
                <Input
                  id="title"
                  value={job.title}
                  onChange={(e) => setJob({ ...job, title: e.target.value })}
                  placeholder="z.B. Senior Software Entwickler"
                  data-testid="job-title-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Standort *</Label>
                  <Input
                    id="location"
                    value={job.location}
                    onChange={(e) => setJob({ ...job, location: e.target.value })}
                    placeholder="z.B. Berlin"
                    data-testid="job-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_type">Jobtyp</Label>
                  <Select 
                    value={job.job_type} 
                    onValueChange={(value) => setJob({ ...job, job_type: value })}
                  >
                    <SelectTrigger data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Vollzeit">Vollzeit</SelectItem>
                      <SelectItem value="Teilzeit">Teilzeit</SelectItem>
                      <SelectItem value="Minijob">Minijob</SelectItem>
                      <SelectItem value="Praktikum">Praktikum</SelectItem>
                      <SelectItem value="Freelance">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="salary_range">Gehaltsspanne</Label>
                  <Input
                    id="salary_range"
                    value={job.salary_range}
                    onChange={(e) => setJob({ ...job, salary_range: e.target.value })}
                    placeholder="z.B. 50.000€ - 70.000€"
                    data-testid="job-salary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="experience_level">Erfahrungslevel</Label>
                  <Select 
                    value={job.experience_level} 
                    onValueChange={(value) => setJob({ ...job, experience_level: value })}
                  >
                    <SelectTrigger data-testid="experience-level-select">
                      <SelectValue placeholder="Wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Berufseinsteiger">Berufseinsteiger</SelectItem>
                      <SelectItem value="Junior">Junior (1-2 Jahre)</SelectItem>
                      <SelectItem value="Mid-Level">Mid-Level (3-5 Jahre)</SelectItem>
                      <SelectItem value="Senior">Senior (5+ Jahre)</SelectItem>
                      <SelectItem value="Lead">Lead / Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="remote"
                  checked={job.remote_option}
                  onCheckedChange={(checked) => setJob({ ...job, remote_option: checked })}
                  data-testid="remote-switch"
                />
                <Label htmlFor="remote">Remote-Arbeit möglich</Label>
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Stellenbeschreibung</CardTitle>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm" 
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="generate-description-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Mit KI generieren
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={job.description}
                onChange={(e) => setJob({ ...job, description: e.target.value })}
                placeholder="Beschreiben Sie die Position..."
                rows={6}
                data-testid="job-description-textarea"
              />
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card>
            <CardHeader>
              <CardTitle>Aufgaben</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {job.tasks.map((task, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={task}
                    onChange={(e) => handleArrayChange('tasks', index, e.target.value)}
                    placeholder="z.B. Entwicklung neuer Features"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleArrayRemove('tasks', index)}
                    disabled={job.tasks.length <= 1}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleArrayAdd('tasks')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Aufgabe hinzufügen
              </Button>
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card>
            <CardHeader>
              <CardTitle>Anforderungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {job.requirements.map((req, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={req}
                    onChange={(e) => handleArrayChange('requirements', index, e.target.value)}
                    placeholder="z.B. 3+ Jahre Erfahrung mit React"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleArrayRemove('requirements', index)}
                    disabled={job.requirements.length <= 1}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleArrayAdd('requirements')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Anforderung hinzufügen
              </Button>
            </CardContent>
          </Card>

          {/* Benefits */}
          <Card>
            <CardHeader>
              <CardTitle>Benefits</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {job.benefits.map((benefit, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={benefit}
                    onChange={(e) => handleArrayChange('benefits', index, e.target.value)}
                    placeholder="z.B. Flexible Arbeitszeiten"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleArrayRemove('benefits', index)}
                    disabled={job.benefits.length <= 1}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleArrayAdd('benefits')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Benefit hinzufügen
              </Button>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="rounded-full flex-1"
              data-testid="submit-job-btn"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Briefcase className="w-4 h-4 mr-2" />
              Stellenanzeige veröffentlichen
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateJob;
